import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { fmtEuro, useApi, STATUS_STYLES } from "@/lib/api";
import { fmtKwp } from "@/lib/apolloCalc";

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <p className="text-xs font-medium uppercase tracking-widest text-slate-500">{label}</p>
    <p data-testid={testId} className="mt-3 font-display text-2xl font-semibold text-slate-900 sm:text-3xl">{value}</p>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => {
    api("get", "/admin/stats").then(setS);
  }, [api]);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const maxStatus = Math.max(1, ...s.by_status.map((b) => b.count));
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Panoramica" subtitle="Andamento di lead e preventivi" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label="Lead totali" value={s.total_leads} testId="kpi-total-leads" />
        <Kpi label="Nuovi" value={s.new_leads} testId="kpi-new-leads" />
        <Kpi label="kWp richiesti" value={fmtKwp(s.total_kwp)} testId="kpi-total-kwp" />
        <Kpi label="Valore pipeline" value={fmtEuro(s.pipeline_value)} testId="kpi-pipeline-value" />
        <Kpi label="Conversione" value={`${s.conversion_rate}%`} testId="kpi-conversion" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <p className="text-sm font-semibold text-slate-900">Lead ultimi 14 giorni</p>
          <div className="mt-4 h-64" data-testid="leads-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.per_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip />
                <Area type="monotone" dataKey="count" name="Lead" stroke="#CA8A04" fill="#F5B700" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-semibold text-slate-900">Pipeline per stato</p>
          <ul className="mt-4 space-y-3">
            {s.by_status.map((b) => (
              <li key={b.status} data-testid={`status-count-${b.status.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex justify-between text-sm"><span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLES[b.status]}`}>{b.status}</span><span className="font-semibold">{b.count}</span></div>
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${(b.count / maxStatus) * 100}%` }} /></div>
              </li>
            ))}
          </ul>
          <div className="mt-6 grid grid-cols-4 gap-2 border-t border-slate-100 pt-4 text-center">
            {s.by_zone.map((z) => (
              <div key={z.zone}><p className="text-xs text-slate-500">{z.zone}</p><p className="font-display text-xl font-semibold">{z.count}</p></div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Kpi label="Preventivi emessi" value={s.quotes_count} testId="kpi-quotes-count" />
        <Kpi label="Valore accettato" value={fmtEuro(s.accepted_value)} testId="kpi-accepted-value" />
      </div>
    </div>
  );
}
